<template>
  <div class="page-container">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">备料</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-segmented v-model="activeTab" :options="tabOptions" @change="handleTabChange" block class="tabs-segment" />

      <div class="modal-search">
        <div class="search-row">
          <el-select
            v-model="searchType"
            class="search-type-select"
            @change="handleSearchTypeChange"
          >
            <el-option label="发运单号" value="resourceNo" />
            <el-option label="供应商" value="supplier" />
          </el-select>

          <template v-if="searchType === 'resourceNo'">
            <el-input
              ref="resourceNoInputRef"
              v-model="resourceNo"
              class="search-keyword-input"
              placeholder="请扫描或输入发运单号"
              clearable
              @input="handleResourceNoInput"
              @keyup.enter="triggerResourceNoSearch"
              @clear="clearResourceNo"
            />
            <el-button
              class="scan-btn"
              type="primary"
              aria-label="扫码"
              @click="startScan"
            >
              <img src="@/assets/扫码.svg" alt="扫码" class="scan-icon" />
            </el-button>
          </template>

          <el-select
            v-else
            v-model="selectedSupplierId"
            class="supplier-select"
            placeholder="请输入供应商名称搜索"
            filterable
            remote
            :remote-method="handleSupplierSearch"
            :loading="supplierLoading"
            clearable
            @change="handleSupplierChange"
            @clear="clearSelectedSupplier"
          >
            <el-option
              v-for="s in supplierList"
              :key="s.id"
              :label="s.supplierName"
              :value="s.id"
            />
          </el-select>
        </div>
      </div>

      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <div class="list-area" v-loading="loading" element-loading-text="加载中...">
        <div v-if="list.length === 0 && !loading" class="empty-state">
          <el-empty description="暂无数据" :image-size="80" />
        </div>

        <div v-else class="card-list">
          <div class="data-card" v-for="item in list" :key="item.id">
            <div class="receive-card-header">
              <div class="receive-type">备料</div>
              <div class="receive-no">送货单号：{{ item.resourceNo || '-' }}</div>
            </div>

            <div class="receive-card-body">
              <div class="receive-cols">
                <div class="receive-col-left">
                  <div class="col-title">物料编码</div>
                  <div v-if="materialNoList(item).length" class="material-code-list">
                    <div v-for="(no, idx) in materialNoList(item)" :key="no + '-' + idx" class="material-code-item">{{ no }}</div>
                  </div>
                  <div v-else class="material-code-empty">-</div>
                </div>

                <div class="receive-col-right">
                  <div class="info-row">
                    <div class="info-label">供应商</div>
                    <div class="info-value">{{ item.supplierName || '-' }}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">仓库</div>
                    <div class="info-value">{{ item.storeName || '-' }}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">主机厂</div>
                    <div class="info-value">{{ item.factoryName || '-' }}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">子库</div>
                    <div class="info-value">{{ item.subWareName || '-' }}</div>
                  </div>
                  <div class="info-row">
                    <div class="info-label">需求日期</div>
                    <div class="info-value">{{ item.demandTime || '-' }}</div>
                  </div>
                </div>
              </div>

              <el-button
                v-if="activeTab === 'todo'"
                type="primary"
                round
                class="start-btn"
                @click="handleStartPrepare(item)"
              >
                开始备料
              </el-button>
            </div>
          </div>

          <div class="list-footer">
            <div class="page-info">已加载 {{ list.length }} / {{ total }}</div>
            <el-button
              type="primary"
              text
              @click="loadMore"
              :loading="loadingMore"
              :disabled="finished"
            >
              {{ finished ? '没有更多了' : (loadingMore ? '加载中...' : '加载更多') }}
            </el-button>
          </div>
        </div>
      </div>
    </main>

    <CameraScan
      v-model:visible="scanVisible"
      title="扫描发运单号"
      box-mode="barcode"
      :formats="scanFormats"
      :result-validator="isValidResourceNo"
      :confirm-count="1"
      tip-text="条码横放进绿框，靠近对焦清晰即可"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { Html5QrcodeSupportedFormats } from 'html5-qrcode'
import toast from '@/utils/toast'
import { getPreparePage } from '@/api/prepare'
import { getSupplierPageGtZero } from '@/api/wareInReceiveCreate'
import CameraScan from '@/components/CameraScan'

// 发运单标签是 CODE_128（如 J26081900WPQBXG00003）
const SCAN_FORMATS = [Html5QrcodeSupportedFormats.CODE_128]

/** 过滤误读乱码；正常如 J26081900WPQBXG00003 */
function isValidResourceNo(text) {
  const val = String(text || '').trim()
  if (val.length < 8 || val.length > 64) return false
  for (let i = 0; i < val.length; i += 1) {
    const code = val.charCodeAt(i)
    if (code <= 0x1f || (code >= 0x7f && code <= 0xff)) return false
  }
  // 字母数字为主，允许少量分隔符
  if (!/^[A-Za-z0-9][A-Za-z0-9\-_/.]*$/.test(val)) return false
  const lettersDigits = (val.match(/[A-Za-z0-9]/g) || []).length
  if (lettersDigits / val.length < 0.85) return false
  return true
}

export default {
  name: 'PrepareList',
  components: { CameraScan },
  setup() {
    const router = useRouter()
    const scanFormats = SCAN_FORMATS

    const activeTab = ref('todo')
    const tabOptions = [
      { label: '待备料', value: 'todo' },
      { label: '已完成', value: 'done' },
      { label: '已取消', value: 'cancel' },
    ]
    const handleTabChange = (val) => {
      switchTab(val)
    }
    const current = ref(1)
    const size = ref(10)

    const list = ref([])
    const total = ref(0)

    const loading = ref(false)
    const loadingMore = ref(false)

    const statusMsg = ref('')
    const statusType = ref('info')

    const searchType = ref('resourceNo')
    const resourceNo = ref('')
    const resourceNoInputRef = ref(null)
    const resourceNoTimer = ref(0)
    const scanVisible = ref(false)

    const supplierKeyword = ref('')
    const supplierLoading = ref(false)
    const supplierList = ref([])
    const selectedSupplier = ref(null)
    const selectedSupplierId = ref('')
    const supplierTimer = ref(0)

    const focusResourceNoInput = () => {
      nextTick(() => {
        const input = resourceNoInputRef.value
        const el = input?.input || input?.$el?.querySelector?.('input') || input
        el?.focus?.()
      })
    }

    const prepareStatus = computed(() => {
      if (activeTab.value === 'todo') return 0
      if (activeTab.value === 'done') return 2
      return 3
    })

    const finished = computed(() => list.value.length >= total.value && total.value > 0)

    const handleBack = () => router.push('/home')
    const goHome = () => router.push('/home')

    const firstMaterialNo = (item) => {
      const arr = item?.prepareMaterialDetailList || []
      if (!Array.isArray(arr) || arr.length === 0) return '-'
      return arr[0]?.materialNo || '-'
    }

    const materialNoList = (item) => {
      const arr =
        item?.prepareMaterialDetailList ||
        item?.prepareDetailList ||
        item?.materialList ||
        item?.detailList ||
        []

      const list = Array.isArray(arr) ? arr : []
      const map = new Map()
      for (let i = 0; i < list.length; i += 1) {
        const no = String(list[i]?.materialNo || '').trim()
        if (!no) continue
        if (!map.has(no)) map.set(no, true)
      }
      return Array.from(map.keys())
    }

    const fetchPage = async ({ reset } = { reset: false }) => {
      if (reset) {
        current.value = 1
        list.value = []
        total.value = 0
      }

      loading.value = reset
      loadingMore.value = !reset
      statusMsg.value = ''

      try {
        const res = await getPreparePage({
          current: current.value,
          size: size.value,
          prepareStatus: prepareStatus.value,
          supplierIds:
            searchType.value === 'supplier' && selectedSupplier.value?.id
              ? String(selectedSupplier.value.id)
              : '',
          resourceNo:
            searchType.value === 'resourceNo'
              ? String(resourceNo.value || '').trim()
              : '',
          materialIds: '',
          factoryIds: '',
          subWareIds: '',
          remark: ''
        })

        const records = res?.data?.records || []
        const nextList = reset ? records : list.value.concat(records)

        list.value = nextList
        total.value = res?.data?.total || nextList.length
      } catch (e) {
        statusMsg.value = e?.message || '加载失败'
        statusType.value = 'error'
      } finally {
        loading.value = false
        loadingMore.value = false
      }
    }

    const handleSearchTypeChange = () => {
      if (resourceNoTimer.value) window.clearTimeout(resourceNoTimer.value)
      if (supplierTimer.value) window.clearTimeout(supplierTimer.value)
      if (searchType.value === 'resourceNo') {
        focusResourceNoInput()
      }
    }

    const triggerResourceNoSearch = async () => {
      if (resourceNoTimer.value) window.clearTimeout(resourceNoTimer.value)
      resourceNo.value = String(resourceNo.value || '').trim()
      await fetchPage({ reset: true })
    }

    const handleResourceNoInput = () => {
      if (resourceNoTimer.value) window.clearTimeout(resourceNoTimer.value)
      const val = String(resourceNo.value || '').trim()
      if (!val) {
        fetchPage({ reset: true })
        return
      }
      resourceNoTimer.value = window.setTimeout(() => {
        triggerResourceNoSearch()
      }, 400)
    }

    const clearResourceNo = async () => {
      if (resourceNoTimer.value) window.clearTimeout(resourceNoTimer.value)
      resourceNo.value = ''
      await fetchPage({ reset: true })
      focusResourceNoInput()
    }

    const startScan = () => {
      scanVisible.value = true
    }

    const handleScanSuccess = async (decodedText) => {
      const text = String(decodedText || '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/[\r\n\t]/g, '')
        .trim()
      if (!text) return

      searchType.value = 'resourceNo'
      resourceNo.value = text
      scanVisible.value = false
      toast.success('扫码成功')
      // 扫码完成后不聚焦输入框，避免弹出键盘
      await nextTick()
      const input = resourceNoInputRef.value
      const el = input?.input || input?.$el?.querySelector?.('input') || input
      el?.blur?.()
      await fetchPage({ reset: true })
    }

    const handleSupplierSearch = async (keyword) => {
      supplierKeyword.value = keyword
      if (!keyword || !keyword.trim()) {
        supplierList.value = []
        return
      }
      if (supplierTimer.value) window.clearTimeout(supplierTimer.value)
      supplierTimer.value = window.setTimeout(async () => {
        await fetchSuppliers()
      }, 250)
    }

    const fetchSuppliers = async () => {
      if (supplierLoading.value) return
      supplierLoading.value = true
      try {
        const res = await getSupplierPageGtZero({
          supplierName: supplierKeyword.value.trim(),
          current: 1,
          size: 20
        })
        supplierList.value = res?.data?.records || []
      } catch (e) {
        supplierList.value = []
        toast.error(e?.message || '供应商加载失败')
      } finally {
        supplierLoading.value = false
      }
    }

    const handleSupplierChange = async (val) => {
      if (val) {
        const s = supplierList.value.find((item) => item.id === val)
        selectedSupplier.value = s || null
        supplierKeyword.value = s?.supplierName || ''
      } else {
        selectedSupplier.value = null
        supplierKeyword.value = ''
      }
      await fetchPage({ reset: true })
    }

    const clearSelectedSupplier = async () => {
      selectedSupplier.value = null
      selectedSupplierId.value = ''
      supplierKeyword.value = ''
      supplierList.value = []
      await fetchPage({ reset: true })
    }

    const switchTab = async (tab) => {
      activeTab.value = tab
      await fetchPage({ reset: true })
    }

    const loadMore = async () => {
      if (finished.value || loading.value || loadingMore.value) return
      current.value += 1
      await fetchPage({ reset: false })
    }

    const handleStartPrepare = (item) => {
      const id = item?.id
      if (!id) {
        toast.error('缺少备料ID')
        return
      }

      try {
        sessionStorage.setItem(
          `wms_prepare_header_${id}`,
          JSON.stringify({
            supplierName: item?.supplierName || '',
            storeName: item?.storeName || '',
            factoryName: item?.factoryName || '',
            subWareName: item?.subWareName || '',
            demandTime: item?.demandTime || ''
          })
        )
      } catch {
        // ignore
      }

      router.push(`/prepare/detail/${id}`)
    }

    onMounted(() => {
      fetchPage({ reset: true })
      focusResourceNoInput()
    })

    onUnmounted(() => {
      if (resourceNoTimer.value) window.clearTimeout(resourceNoTimer.value)
      if (supplierTimer.value) window.clearTimeout(supplierTimer.value)
    })

    return {
      activeTab,
      tabOptions,
      handleTabChange,
      list,
      total,
      loading,
      loadingMore,
      finished,
      statusMsg,
      statusType,
      searchType,
      resourceNo,
      resourceNoInputRef,
      scanVisible,
      scanFormats,
      isValidResourceNo,
      handleSearchTypeChange,
      handleResourceNoInput,
      triggerResourceNoSearch,
      clearResourceNo,
      startScan,
      handleScanSuccess,
      supplierKeyword,
      supplierLoading,
      supplierList,
      selectedSupplier,
      selectedSupplierId,
      handleSupplierSearch,
      handleSupplierChange,
      clearSelectedSupplier,
      handleBack,
      goHome,
      switchTab,
      loadMore,
      handleStartPrepare,
      firstMaterialNo,
      materialNoList
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  width: 36px;
  height: 36px;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

.tabs-segment {
  margin-bottom: 8px;
}

.tabs-segment :deep(.el-segmented) {
  border-radius: 12px;
  padding: 3px;
}

.tabs-segment :deep(.el-segmented__item) {
  border-radius: 10px;
  padding: 8px 0;
  margin: 0;
}

.modal-search {
  margin-bottom: 8px;
}

.search-row {
  display: flex;
  align-items: center;
}

.search-type-select {
  width: auto;
  flex: 0 0 auto;
}

.search-type-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 8px 0 0 8px;
}

.search-type-select :deep(.el-select__wrapper.is-focus) {
  box-shadow: none;
}

.search-keyword-input,
.supplier-select {
  flex: 1;
  min-width: 0;
}

.search-keyword-input :deep(.el-input__wrapper) {
  height: 38px;
  border-radius: 0;
  border-left: none;
}

.supplier-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 0 8px 8px 0;
  border-left: none;
}

.supplier-select :deep(.el-select__wrapper.is-focus) {
  box-shadow: none;
}

.scan-btn {
  flex: 0 0 38px;
  width: 38px;
  min-width: 38px;
  height: 38px;
  padding: 0;
  margin: 0;
  border-radius: 0 8px 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-icon {
  width: 18px;
  height: 18px;
  display: block;
  filter: brightness(0) invert(1);
}

.status-banner {
  margin: 8px 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
}

.status-banner.info {
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.status-banner.error {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.list-area {
  min-height: 100px;
}

.empty-state {
  text-align: center;
  padding: 30px 0;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.data-card {
  background: var(--card-background);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-light);
  border: 1px solid var(--border-color);
}

.receive-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: rgba(35, 177, 192, 0.08);
  border-bottom: 1px solid var(--border-color);
}

.receive-type {
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
}

.receive-no {
  color: var(--text-primary);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.receive-card-body {
  padding: 8px;
}

.receive-cols {
  display: flex;
  gap: 8px;
}

.receive-col-left,
.receive-col-right {
  background: var(--background-color);
  border-radius: 8px;
  padding: 8px;
}

.receive-col-left {
  flex: 0 0 40%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.receive-col-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.col-title {
  font-size: 11px;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.material-code-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.material-code-item {
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-color);
  word-break: break-all;
  line-height: 1.2;
}

.material-code-empty {
  font-size: 12px;
  color: var(--primary-color);
}

.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
}

.info-label {
  flex: 0 0 auto;
  color: var(--text-secondary);
  font-size: 11px;
}

.info-value {
  flex: 1;
  min-width: 0;
  text-align: right;
  color: var(--text-primary);
  font-size: 12px;
  word-break: break-all;
  line-height: 1.2;
}

.start-btn {
  width: 100%;
  margin-top: 8px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  color: var(--text-secondary);
  font-size: 12px;
}
</style>
